/**
 * Motor de sincronización del cliente (FR-SYNC-08..12).
 * - Envía la cola de salida en lotes; el servidor es idempotente por opId.
 * - Descarga cambios desde un cursor y los fusiona por HLC.
 * - Reintenta con retroceso exponencial y aleatoriedad (máximo 5 min).
 */
import { ENTITIES, PROTOCOL_VERSION, opFromRecord, type EntityName, type OpResult, type PullResponse, type PushResponse } from '@habitos/protocol';
import { db, getMeta, setMeta } from '../db/db';
import { allRecords, applyRemote, deviceId, wipeLocalData } from '../db/repo';
import { uuidv7 } from '../lib/ids';
import { api, ApiError, NetworkError } from './api';
import { getSyncState, setSyncState } from './store';

const BATCH = 200;
const MAX_BACKOFF = 5 * 60_000;

let running: Promise<void> | null = null;
let again = false;
let failures = 0;
let retryTimer: number | null = null;

export interface SessionInfo {
  user: { id: string; email: string };
  entitlement: { plan: 'free' | 'premium'; currentPeriodEnd: string | null; status: string; cancelAtPeriodEnd: boolean; provider: string };
}

export async function cacheEntitlement(e: SessionInfo['entitlement']): Promise<void> {
  if (e.plan === 'premium') {
    const end = e.currentPeriodEnd ? Date.parse(e.currentPeriodEnd) : Date.now() + 7 * 86_400_000;
    // Caché local con caducidad para permitir el uso sin conexión (FR-BIZ-02).
    await setMeta('entitlement', { ...e, plan: 'premium', validUntil: Math.min(end, Date.now() + 7 * 86_400_000) });
  } else {
    await setMeta('entitlement', { ...e, plan: 'free', validUntil: 0 });
  }
}

async function refreshCounts(): Promise<void> {
  setSyncState({ pending: await db.outbox.count(), rejected: await db.rejected.count() });
}

/** Comprueba la sesión al arrancar. No falla si no hay servidor. */
export async function bootstrapSession(): Promise<void> {
  const accountUserId = await getMeta<string | null>('accountUserId', null);
  setSyncState({ signedIn: accountUserId !== null, email: await getMeta<string | null>('accountEmail', null), lastSyncAt: await getMeta<number | null>('lastSyncAt', null) });
  await refreshCounts();
  try {
    const s = await api<SessionInfo>('/auth/session');
    setSyncState({ serverAvailable: true });
    if (accountUserId && s.user.id !== accountUserId) {
      // La sesión pertenece a otra cuenta: no se mezclan datos.
      await signOutLocal(false);
      return;
    }
    if (accountUserId) await cacheEntitlement(s.entitlement);
  } catch (err) {
    if (err instanceof ApiError) {
      setSyncState({ serverAvailable: true });
      if (err.status === 401 && accountUserId) setSyncState({ phase: 'error', lastError: 'session_expired' });
    } else {
      setSyncState({ serverAvailable: false, phase: accountUserId ? 'offline' : 'idle' });
    }
  }
}

export function requestSync(delayMs = 0): void {
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    void syncNow();
  }, delayMs);
}

export async function syncNow(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = (async () => {
    do {
      again = false;
      await syncOnce();
    } while (again);
  })().finally(() => {
    running = null;
  });
  return running;
}

async function syncOnce(): Promise<void> {
  if ((await getMeta<string | null>('accountUserId', null)) === null) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setSyncState({ phase: 'offline' });
    return;
  }
  setSyncState({ phase: 'syncing' });
  try {
    await pushAll();
    await pullAll();
    failures = 0;
    const now = Date.now();
    await setMeta('lastSyncAt', now);
    setSyncState({ phase: 'idle', lastSyncAt: now, lastError: null, serverAvailable: true });
  } catch (err) {
    failures++;
    const delay = Math.min(MAX_BACKOFF, 1000 * 2 ** Math.min(failures, 9)) * (0.5 + Math.random() / 2);
    if (err instanceof ApiError && err.status === 401) {
      setSyncState({ phase: 'error', lastError: 'session_expired' });
      return;
    }
    if (err instanceof ApiError && err.status === 426) {
      setSyncState({ phase: 'error', lastError: 'update_required' });
      return;
    }
    setSyncState({ phase: err instanceof NetworkError ? 'offline' : 'error', lastError: err instanceof Error ? err.message : String(err) });
    requestSync(delay);
  } finally {
    await refreshCounts();
  }
}

async function pushAll(): Promise<void> {
  const dev = await deviceId();
  for (;;) {
    const batch = await db.outbox.orderBy('seq').limit(BATCH).toArray();
    if (batch.length === 0) return;
    const res = await api<PushResponse>('/sync/push', {
      body: { protocol: PROTOCOL_VERSION, deviceId: dev, ops: batch.map((o) => ({ opId: o.opId, entity: o.entity, id: o.id, fields: o.fields })) },
    });
    const byId = new Map<string, OpResult>(res.results.map((r) => [r.opId, r]));
    let skew = false;
    await db.transaction('rw', db.outbox, db.rejected, async () => {
      for (const item of batch) {
        const r = byId.get(item.opId);
        if (!r) continue;
        if (r.status === 'applied' || r.status === 'duplicate') {
          await db.outbox.delete(item.seq!);
        } else if (r.code === 'clock_skew') {
          skew = true;
          await db.outbox.update(item.seq!, { attempts: item.attempts + 1, lastError: 'clock_skew' });
        } else {
          await db.outbox.delete(item.seq!);
          await db.rejected.add({ ...item, seq: undefined, code: r.code ?? 'rejected', detail: r.detail, rejectedAt: Date.now() });
        }
      }
    });
    if (skew) throw new Error('clock_skew');
  }
}

/**
 * Entidades que este cliente sabe guardar. Si son más que en la última sincronización
 * (una actualización de la app), el servidor pudo omitir registros de tipos nuevos:
 * se vuelve a descargar desde 0 una vez (la fusión es idempotente).
 */
export async function prepareSyncEntities(): Promise<void> {
  const known = await getMeta<string[] | null>('syncEntities', null);
  if (!known || ENTITIES.some((e) => !known.includes(e))) {
    await setMeta('syncCursor', 0);
    await setMeta('syncEntities', [...ENTITIES]);
  }
}

async function pullAll(): Promise<void> {
  await prepareSyncEntities();
  let cursor = await getMeta<number>('syncCursor', 0);
  for (;;) {
    const res = await api<PullResponse>(`/sync/pull?cursor=${cursor}&limit=500&entities=${ENTITIES.join(',')}`);
    if (res.records.length) await applyRemote(res.records, { enqueue: false });
    cursor = res.cursor;
    await setMeta('syncCursor', cursor);
    if (!res.hasMore) return;
  }
}

/** ¿La cuenta ya tiene datos en el servidor? */
export async function serverHasData(): Promise<boolean> {
  const res = await api<PullResponse>('/sync/pull?cursor=0&limit=1');
  return res.records.length > 0;
}

export type LinkDecision = 'merge' | 'ask' | 'ask_other_account';

/**
 * Qué hacer con los datos locales al iniciar sesión (FR-SYNC-08).
 * Si los datos del dispositivo son de otra cuenta (dispositivo compartido), siempre se
 * pregunta: subirlos sin avisar copiaría los datos de una persona en la cuenta de otra.
 */
export function decideLink(o: { hasLocal: boolean; hasRemote: boolean; previousUserId: string | null; userId: string }): LinkDecision {
  if (!o.hasLocal) return 'merge';
  if (o.previousUserId !== null && o.previousUserId !== o.userId) return 'ask_other_account';
  return o.hasRemote ? 'ask' : 'merge';
}

export interface PreviousAccount {
  id: string;
  email: string | null;
}

/** Cuenta a la que pertenecían los datos locales tras cerrar sesión sin borrarlos. */
export function previousAccount(): Promise<PreviousAccount | null> {
  return getMeta<PreviousAccount | null>('previousAccount', null);
}

export interface RejectedChange {
  entity: EntityName;
  id: string;
  code: string;
  detail?: string;
  /** Nombre del hábito o grupo afectado, si sigue existiendo en el dispositivo. */
  name: string | null;
}

/** Cambios rechazados por el servidor, uno por registro, para explicarlos al usuario. */
export async function rejectedChanges(): Promise<RejectedChange[]> {
  const out: RejectedChange[] = [];
  const seen = new Set<string>();
  for (const r of await db.rejected.orderBy('seq').toArray()) {
    const key = `${r.entity}:${r.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const local = await db.table_(r.entity).get(r.id);
    const name = local && typeof local.data.name === 'string' ? local.data.name : null;
    out.push({ entity: r.entity, id: r.id, code: r.code, detail: r.detail, name });
  }
  return out;
}

/**
 * Vuelve a enviar los registros rechazados (por ejemplo, tras liberar espacio del plan).
 * Se envía el estado actual de cada registro con un opId nuevo: el servidor recuerda los
 * opId ya procesados y no reevaluaría los antiguos.
 */
export async function retryRejected(): Promise<number> {
  const rejected = await db.rejected.toArray();
  const ops: ReturnType<typeof opFromRecord>[] = [];
  const seen = new Set<string>();
  for (const r of rejected) {
    const key = `${r.entity}:${r.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const local = await db.table_(r.entity).get(r.id);
    if (local) ops.push(opFromRecord(uuidv7(), { entity: r.entity, id: local.id, data: local.data, hlc: local.hlc }));
  }
  await db.transaction('rw', db.outbox, db.rejected, async () => {
    for (const op of ops) await db.outbox.add({ ...op, createdAt: Date.now(), attempts: 0 });
    await db.rejected.clear();
  });
  await refreshCounts();
  if (ops.length > 0) requestSync(0);
  return ops.length;
}

/** Cambios locales que aún no han llegado al servidor. */
export function pendingChanges(): Promise<number> {
  return db.outbox.count();
}

/**
 * Activa la sincronización tras iniciar sesión (FR-SYNC-08).
 * mode = 'merge': sube todos los datos locales (idempotente) y descarga los del servidor.
 * mode = 'discard': borra los datos locales y descarga los de la cuenta.
 */
export async function linkAccount(session: SessionInfo, mode: 'merge' | 'discard'): Promise<void> {
  if (mode === 'discard') await wipeLocalData();
  // Las conversaciones del asistente son de quien las tuvo: no pasan a la nueva cuenta.
  await clearAssistant();
  await db.meta.delete('previousAccount');
  await setMeta('accountUserId', session.user.id);
  await setMeta('accountEmail', session.user.email);
  await setMeta('syncCursor', 0);
  await cacheEntitlement(session.entitlement);
  if (mode === 'merge') {
    const records = await allRecords();
    await db.transaction('rw', db.outbox, async () => {
      for (const r of records) await db.outbox.add({ ...opFromRecord(uuidv7(), r), createdAt: Date.now(), attempts: 0 });
    });
  }
  setSyncState({ signedIn: true, email: session.user.email });
  await syncNow();
}

/** Borra las conversaciones del asistente (carga diferida: la sincronización no depende de él). */
async function clearAssistant(): Promise<void> {
  await (await import('../assistant/store')).clearAllConversations();
}

/** Cierra la sesión en este dispositivo. Los datos locales se conservan salvo que se pida borrarlos. */
export async function signOutLocal(wipe: boolean): Promise<void> {
  const owner = await getMeta<string | null>('accountUserId', null);
  const email = await getMeta<string | null>('accountEmail', null);
  if (wipe) await wipeLocalData();
  await clearAssistant();
  await db.transaction('rw', db.outbox, db.rejected, db.meta, async () => {
    await db.outbox.clear();
    await db.rejected.clear();
    for (const k of ['accountUserId', 'accountEmail', 'syncCursor', 'lastSyncAt', 'entitlement', 'previousAccount']) await db.meta.delete(k);
    // Los datos que se quedan en el dispositivo siguen siendo de esta cuenta.
    if (!wipe && owner) await setMeta('previousAccount', { id: owner, email });
  });
  setSyncState({ signedIn: false, email: null, phase: 'idle', pending: 0, rejected: 0, lastSyncAt: null, lastError: null });
}

export function syncStateSnapshot() {
  return getSyncState();
}
